// Search by any part of the product code (SKU), from 3 characters.
// Shopify search only matches a whole code or its beginning, so this adds substring matching
// on top of it, using the index from templates/collection.sku-index.liquid and the cards from
// templates/product.sku-card.liquid. Used by the header dropdown (global.js) and the search page.
(function () {
  const MIN_LENGTH = 3;
  const DROPDOWN_SIZE = 5;
  const PAGE_SIZE = 24;
  const CACHE_KEY = "mcSkuIndex:v1";
  const CACHE_TTL = 6 * 60 * 60 * 1000;
  let indexPromise = null;

  function normalize(value) {
    return String(value || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  }

  function fetchIndexPage(page) {
    return fetch("/collections/all?view=sku-index&page=" + page, { credentials: "same-origin" }).then(function (response) {
      if (!response.ok) throw new Error("sku-index " + response.status);
      return response.json();
    });
  }

  // [[handle, "NORMALIZEDSKU1 NORMALIZEDSKU2"], ...], cached in localStorage for CACHE_TTL.
  function loadIndex() {
    if (indexPromise) return indexPromise;

    try {
      const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || "null");
      if (cached && Date.now() - cached.time < CACHE_TTL) return (indexPromise = Promise.resolve(cached.items));
    } catch (e) {}

    indexPromise = fetchIndexPage(1)
      .then(function (first) {
        const rest = [];
        for (let page = 2; page <= first.pages; page++) rest.push(fetchIndexPage(page));
        return Promise.all(rest).then(function (pages) {
          return pages.reduce(function (all, page) { return all.concat(page.items); }, first.items);
        });
      })
      .then(function (items) {
        items = items.map(function (item) {
          return [item[0], item[1].split(/\s+/).map(normalize).filter(Boolean).join(" ")];
        });
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify({ time: Date.now(), items: items }));
        } catch (e) {}
        return items;
      })
      .catch(function (error) {
        indexPromise = null;
        throw error;
      });
    return indexPromise;
  }

  // Products with a code containing the term; prefix = a code starts with it (Shopify search finds those itself).
  function find(term) {
    const query = normalize(term);
    if (query.length < MIN_LENGTH) return Promise.resolve([]);

    return loadIndex()
      .then(function (items) {
        return items
          .filter(function (item) { return item[1].indexOf(query) !== -1; })
          .map(function (item) {
            return {
              handle: item[0],
              prefix: item[1].split(" ").some(function (sku) { return sku.indexOf(query) === 0; })
            };
          });
      })
      .catch(function () { return []; });
  }

  function cards(handles) {
    return Promise.all(handles.map(function (handle) {
      return fetch("/products/" + encodeURIComponent(handle) + "?view=sku-card", { credentials: "same-origin" })
        .then(function (response) { return response.ok ? response.text() : ""; })
        .then(function (html) {
          const template = document.createElement("template");
          template.innerHTML = html.trim();
          return template.content.querySelector("[data-sku-card]");
        })
        .catch(function () { return null; });
    })).then(function (elements) { return elements.filter(Boolean); });
  }

  function handleFromUrl(url) {
    return String(url || "").split("?")[0].split("/products/")[1] || "";
  }

  // Code matches go first when the term looks like a code or they outnumber Shopify's own results.
  function codeFirst(term, codeMatches, nativeCount) {
    return /\d/.test(term) || codeMatches >= nativeCount;
  }

  // Merges code matches into the dropdown data ({results_count, results}) returned by search.json.liquid.
  function suggest(term, data) {
    return find(term)
      .then(function (matches) {
        const nativeHandles = data.results.map(function (item) { return handleFromUrl(item.url); });
        const extra = matches.filter(function (match) {
          return !(data.results_count > 0 && match.prefix) && nativeHandles.indexOf(match.handle) === -1;
        });
        if (!extra.length) return data;

        const first = codeFirst(term, extra.length, data.results_count);
        const take = first ? DROPDOWN_SIZE : Math.max(0, DROPDOWN_SIZE - data.results.length);

        return cards(extra.slice(0, take).map(function (match) { return match.handle; })).then(function (elements) {
          const items = elements.map(function (el) {
            return { title: el.dataset.title, price: el.dataset.price, url: el.dataset.url, thumbnail: el.dataset.thumbnail };
          });
          return {
            results_count: data.results_count + extra.length,
            results: (first ? items.concat(data.results) : data.results.concat(items)).slice(0, DROPDOWN_SIZE)
          };
        });
      })
      .catch(function () { return data; });
  }

  // Search page: extra block with products whose code contains the term (sections/search-template.liquid).
  function initSearchPage() {
    const box = document.querySelector("[data-sku-search]");
    if (!box) return;

    const term = box.dataset.terms;
    const nativeCount = parseInt(box.dataset.resultsCount, 10) || 0;
    const exclude = (box.dataset.exclude || "").split(" ");
    const grid = box.querySelector("[data-sku-grid]");
    const more = box.querySelector("[data-sku-more]");

    find(term).then(function (matches) {
      matches = matches.filter(function (match) {
        return exclude.indexOf(match.handle) === -1 && !(nativeCount > 0 && match.prefix);
      });
      if (!matches.length) return;

      let shown = 0;
      function showNext() {
        const batch = matches.slice(shown, shown + PAGE_SIZE).map(function (match) { return match.handle; });
        shown += batch.length;
        more.style.display = "none";
        return cards(batch).then(function (elements) {
          elements.forEach(function (el) { grid.appendChild(el); });
          if (shown < matches.length) more.style.display = "";
        });
      }

      more.addEventListener("click", showNext);
      showNext().then(function () {
        if (!grid.children.length) return;

        box.querySelector("[data-sku-term]").textContent = term;
        box.style.display = "";

        const nativeGrid = document.querySelector("[data-search-native]");
        if (nativeGrid && codeFirst(term, matches.length, nativeCount)) nativeGrid.parentNode.insertBefore(box, nativeGrid);

        document.querySelectorAll("[data-search-empty]").forEach(function (el) { el.style.display = "none"; });
        const heading = document.querySelector("[data-search-heading]");
        const total = nativeCount + matches.length;
        if (heading) heading.textContent = total + (total === 1 ? " rezultat" : " rezultate") + " pentru „" + term + "”";
      });
    });
  }

  window.mcSkuSearch = { find: find, suggest: suggest, minLength: MIN_LENGTH };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initSearchPage);
  } else {
    initSearchPage();
  }
})();
